import React, { useState, useEffect, useRef } from "react";
import { Bell, Check } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { markAllRead as apiMarkAllRead, markNotificationRead } from "../api/api";
import { useNotifications } from "../hooks/queries";
import { useAuth } from "../hooks/useAuth";
import { Notification } from "../api/api";
import { NotificationTypeIcon } from "./NotificationTypeIcon";
import BranchAccessRequestPopup from "./BranchAccessRequestPopup";

function timeAgo(dateStr: string): string {
  const now = new Date();
  const then = new Date(dateStr);
  const diff = Math.floor((now.getTime() - then.getTime()) / 1000);

  if (diff < 60) return "Zojuist";
  if (diff < 3600) return `${Math.floor(diff / 60)} min geleden`;
  if (diff < 86400) return `${Math.floor(diff / 3600)} uur geleden`;
  return `${Math.floor(diff / 86400)} dag${Math.floor(diff / 86400) === 1 ? "" : "en"} geleden`;
}

export default function NotificationBell() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const { data } = useNotifications();
  const notifications: Notification[] = data ?? [];
  const [open, setOpen] = useState(false);
  const [branchRequestId, setBranchRequestId] = useState<number | null>(null);
  const ref = useRef<HTMLDivElement>(null);

  const unreadCount = notifications.filter((n) => !n.is_read).length;

  // Close dropdown on outside click
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    if (open) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [open]);

  async function markAllRead() {
    try {
      await apiMarkAllRead();
      queryClient.invalidateQueries({ queryKey: ["notifications"] });
    } catch {
      // ignore
    }
  }

  async function handleNotificationClick(n: Notification) {
    if (!n.is_read) {
      await markNotificationRead(n.id);
      queryClient.invalidateQueries({ queryKey: ["notifications"] });
    }
    setOpen(false);
    // Only reviewers can open the access request popup; older grant/deny
    // result notifications sent to HMPs still carry the link.
    if (n.related_branch_access_request_id && user?.role !== "hmp") {
      setBranchRequestId(n.related_branch_access_request_id);
    } else if (n.related_vacature_id) {
      navigate(`/vacature/${n.related_vacature_id}`);
    } else if (n.type === "approval_reminder") {
      navigate("/mail-goedkeuring");
    } else if (n.type === "sync_problem" && user?.role === "admin") {
      navigate("/sheets-sync");
    }
  }

  return (
    <>
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen((v) => !v)}
        className="relative flex h-11 w-11 items-center justify-center rounded-pill border border-waves-line bg-white text-waves-ink shadow-soft transition-colors hover:bg-waves-mist"
        aria-label="Meldingen"
      >
        <Bell size={19} />
        {unreadCount > 0 && (
          <span className="absolute -right-1 -top-1 inline-flex h-5 min-w-5 items-center justify-center rounded-pill border-2 border-white bg-waves-koraal-hover px-1 text-xs font-bold leading-none text-white">
            {unreadCount > 9 ? "9+" : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 top-full z-50 mt-2 w-[calc(100vw-2rem)] overflow-hidden rounded-card border border-waves-line bg-white shadow-pop md:w-96">
          {/* Header */}
          <div className="flex items-center justify-between border-b border-waves-line px-5 py-4">
            <h3 className="text-base font-black tracking-tight text-waves-ink">Meldingen</h3>
            {unreadCount > 0 && (
              <span className="rounded-pill bg-status-alert-bg px-2.5 py-0.5 text-xs font-bold text-status-alert-fg">
                {unreadCount} ongelezen
              </span>
            )}
          </div>

          {/* List */}
          <div className="max-h-[60vh] md:max-h-80 overflow-y-auto">
            {notifications.length === 0 ? (
              <div className="px-5 py-10 text-center text-sm text-waves-muted">
                Geen meldingen
              </div>
            ) : (
              <ul className="divide-y divide-waves-line">
                {notifications.slice(0, 20).map((n) => (
                  <li
                    key={n.id}
                    onClick={() => handleNotificationClick(n)}
                    className={`flex cursor-pointer items-start gap-3 px-5 py-3.5 transition-colors ${
                      !n.is_read ? "bg-waves-mist/60 hover:bg-waves-mist" : "hover:bg-waves-subtle"
                    }`}
                  >
                    <NotificationTypeIcon type={n.type} unread={!n.is_read} size="sm" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm leading-snug text-waves-ink">{n.message}</p>
                      <p className="mt-0.5 font-mono text-xs text-waves-muted">{timeAgo(n.created_at)}</p>
                    </div>
                    {!n.is_read && (
                      <div className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-waves-zee" />
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between border-t border-waves-line px-5 py-3">
            {unreadCount > 0 ? (
              <button
                onClick={markAllRead}
                className="flex items-center gap-1.5 text-[13px] font-bold text-waves-ink transition-colors hover:text-waves-accent"
              >
                <Check size={13} />
                Alles gelezen
              </button>
            ) : <span />}
            <button
              onClick={() => { setOpen(false); navigate("/meldingen"); }}
              className="text-[13px] font-bold text-waves-ink hover:text-waves-accent"
            >
              Bekijk alle →
            </button>
          </div>
        </div>
      )}
    </div>
    <BranchAccessRequestPopup requestId={branchRequestId} onClose={() => setBranchRequestId(null)} />
    </>
  );
}
